import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MaskLines, Reveal } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { api, money } from "@/lib/api";

const CATS = [
  { id: "all", label: "All" },
  { id: "terminals", label: "POS Terminals" },
  { id: "setup", label: "Setup & Gateway" },
  { id: "processing", label: "Processing" },
  { id: "crossborder", label: "Cross-Border" },
];

function ProductCard({ p, onOpen }) {
  const { add } = useCart();
  return (
    <motion.article layout className="group relative flex flex-col border border-white/10 bg-[#0d1524] transition-colors duration-300 hover:border-sky-400/40" data-testid={`product-card-${p.id}`}>
      <button onClick={() => onOpen(p)} className="relative aspect-[4/3] overflow-hidden clip-notch" data-testid={`product-open-${p.id}`}>
        <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,transparent_40%,rgba(8,11,17,0.7))]" />
        <span className={`absolute left-4 top-4 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${p.risk === "High risk" ? "bg-[var(--gold)] text-black" : "bg-white/90 text-black"}`}>{p.risk}</span>
      </button>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-xl font-medium">{p.name}</h3>
        <p className="mt-1 text-sm text-slate-300">{p.tagline}</p>
        <div className="mt-auto flex items-end justify-between pt-6">
          {p.price != null ? (
            <>
              <span className="font-display text-2xl font-semibold" data-testid={`product-price-${p.id}`}>{money(p.price)}</span>
              <button onClick={() => add(p)} data-testid={`add-to-cart-${p.id}`}
                className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-[var(--gold)]">
                <Plus className="h-4 w-4" /> Add
              </button>
            </>
          ) : (
            <>
              <span className="font-mono text-xs text-slate-400">Quoted after underwriting</span>
              <Link to={`/apply?product=${p.id}`} data-testid={`apply-product-${p.id}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm hover:border-sky-400">
                Apply <ArrowUpRight className="h-4 w-4" />
              </Link>
            </>
          )}
        </div>
      </div>
    </motion.article>
  );
}

export default function Store() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(null);
  const { add } = useCart();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => setProducts([]));
  }, []);

  const list = cat === "all" ? products : products.filter((p) => p.category === cat);

  return (
    <main className="mx-auto max-w-[1400px] px-5 pb-28 pt-36 sm:px-10" data-testid="store-page">
      <p className="eyebrow">MidHq Storefront</p>
      <h1 className="mt-5 font-display text-5xl font-semibold leading-[0.95] tracking-[-0.03em] sm:text-7xl">
        <MaskLines lines={["Equip the counter.", { text: "Open the corridor.", className: "text-slate-400 font-light" }]} />
      </h1>
      <div className="mt-12 flex flex-wrap gap-2" data-testid="store-category-tabs">
        {CATS.map((c) => (
          <button key={c.id} onClick={() => setCat(c.id)} data-testid={`store-tab-${c.id}`}
            className={`relative rounded-full px-5 py-2.5 text-sm transition-colors ${cat === c.id ? "text-black" : "text-slate-300 hover:text-white"}`}>
            {cat === c.id && <motion.span layoutId="store-tab-pill" className="absolute inset-0 rounded-full bg-white" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
            <span className="relative">{c.label}</span>
          </button>
        ))}
      </div>
      <Reveal className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((p) => <ProductCard key={p.id} p={p} onOpen={setOpen} />)}
      </Reveal>
      {products.length === 0 && <p className="mt-10 text-slate-400" data-testid="store-empty">Loading catalog…</p>}

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-2xl border-white/10 bg-[#0b111c] p-0" data-testid="product-dialog">
          {open && (
            <div className="grid sm:grid-cols-2">
              <img src={open.image} alt={open.name} className="h-56 w-full object-cover sm:h-full" />
              <div className="p-7">
                <DialogTitle className="font-display text-2xl">{open.name}</DialogTitle>
                <DialogDescription className="mt-1 text-slate-300">{open.tagline}</DialogDescription>
                <ul className="mt-6 space-y-2 text-sm text-slate-200">
                  {(open.specs || []).map((s) => <li key={s} className="flex gap-3"><span className="text-[var(--gold)]">—</span>{s}</li>)}
                </ul>
                <div className="mt-8">
                  {open.price != null ? (
                    <button className="btn-primary" data-testid="dialog-add-to-cart" onClick={() => { add(open); setOpen(null); }}>Add to cart · {money(open.price)}</button>
                  ) : (
                    <Link className="btn-primary" to={`/apply?product=${open.id}`} data-testid="dialog-apply">Apply for this</Link>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
